"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ControlledAccordion = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _identifier = require("../../util/identifier");
var _AccordionItem = require("../AccordionItem/AccordionItem.context");
var _Accordion = require("./Accordion.styles");
var _getNextState = require("./getNextState");
var _excluded = ["children", "expanded", "onChange", "onPanelFocus"];
/**
 * See `Accordion` for documentation.
 */

var ControlledAccordion = /*#__PURE__*/(0, _react.forwardRef)(function (_ref, forwardedRef) {
  var children = _ref.children,
    expanded = _ref.expanded,
    onChange = _ref.onChange,
    onPanelFocus = _ref.onPanelFocus,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  var childrenCount = _react.Children.count(children);

  // Manage panel header focus
  var _useState = (0, _react.useState)(null),
    _useState2 = (0, _slicedToArray2.default)(_useState, 2),
    focusedPanelIndex = _useState2[0],
    setFocusedPanelIndex = _useState2[1];

  // Ref callback to focus on selected panel header
  var focusRef = (0, _react.useCallback)(function (element) {
    if (element) {
      element.focus();
      if (typeof onPanelFocus === 'function') {
        onPanelFocus(focusedPanelIndex);
      }
    }
  }, [focusedPanelIndex, onPanelFocus]);

  // Focus on the first panel header
  var onFocusFirst = (0, _react.useCallback)(function () {
    setFocusedPanelIndex(0);
  }, [setFocusedPanelIndex]);

  // Focus on the last panel header
  var onFocusLast = (0, _react.useCallback)(function () {
    setFocusedPanelIndex(childrenCount - 1);
  }, [childrenCount, setFocusedPanelIndex]);

  // Focus on the next or first panel header
  var onFocusNext = (0, _react.useCallback)(function () {
    if (focusedPanelIndex !== null && focusedPanelIndex >= childrenCount - 1) {
      onFocusFirst();
    } else {
      setFocusedPanelIndex(focusedPanelIndex + 1);
    }
  }, [childrenCount, focusedPanelIndex, onFocusFirst, setFocusedPanelIndex]);

  // Focus on the previous or last panel header
  var onFocusPrevious = (0, _react.useCallback)(function () {
    if (focusedPanelIndex !== null && focusedPanelIndex <= 0) {
      onFocusLast();
    } else {
      setFocusedPanelIndex(focusedPanelIndex - 1);
    }
  }, [focusedPanelIndex, onFocusLast, setFocusedPanelIndex]);

  // Reset panel header focus on blur
  var onBlur = (0, _react.useCallback)(function () {
    setFocusedPanelIndex(null);
  }, [setFocusedPanelIndex]);

  // Call `onChange` prop with selected panel index and next state
  var _onClick = (0, _react.useCallback)(function (panelIndex) {
    onChange(panelIndex, (0, _getNextState.getNextState)(panelIndex, expanded));
  }, [expanded, onChange]);

  // Focus on selected panel header
  var _onFocus = (0, _react.useCallback)(function (panelIndex) {
    setFocusedPanelIndex(panelIndex);
  }, [setFocusedPanelIndex]);

  // Manage keyboard navigation
  var _onKeyDown = (0, _react.useCallback)(function (panelIndex, event) {
    if (event.keyCode === _constants.KEY_CODES.SPACE) {
      _onClick(panelIndex);
    } else if (event.keyCode === _constants.KEY_CODES.ENTER) {
      _onClick(panelIndex);
    } else if (event.keyCode === _constants.KEY_CODES.ARROW_DOWN) {
      onFocusNext();
    } else if (event.keyCode === _constants.KEY_CODES.ARROW_UP) {
      onFocusPrevious();
    } else if (event.keyCode === _constants.KEY_CODES.HOME) {
      onFocusFirst();
    } else if (event.keyCode === _constants.KEY_CODES.END) {
      onFocusLast();
    } else {
      return false;
    }
    event.preventDefault();
    return true;
  }, [_onClick, onFocusNext, onFocusPrevious, onFocusFirst, onFocusLast]);

  // Provider context for each accordion item
  var itemContext = (0, _react.useCallback)(function (panelIndex) {
    return {
      expanded: Array.isArray(expanded) ? expanded.indexOf(panelIndex) !== -1 : expanded === panelIndex,
      headerId: (0, _identifier.identifier)(),
      panelId: (0, _identifier.identifier)(),
      ref: focusedPanelIndex === panelIndex ? focusRef : undefined,
      onBlur: onBlur,
      onClick: function onClick() {
        return _onClick(panelIndex);
      },
      onFocus: function onFocus() {
        return _onFocus(panelIndex);
      },
      onKeyDown: function onKeyDown(event) {
        return _onKeyDown(panelIndex, event);
      }
    };
  }, [expanded, focusRef, focusedPanelIndex, onBlur, _onClick, _onFocus, _onKeyDown]);

  // Generate accordion items with context based on child index
  var items = (0, _react.useMemo)(function () {
    return _react.Children.map(children, function (child, index) {
      return child && /*#__PURE__*/_react.default.createElement(_AccordionItem.AccordionItemContext.Provider, {
        value: itemContext(index)
      }, child);
    });
  }, [children, itemContext]);

  // Render
  return /*#__PURE__*/_react.default.createElement(_Accordion.StyledAccordion, (0, _extends2.default)({
    ref: forwardedRef
  }, rest), items);
});
exports.ControlledAccordion = ControlledAccordion;
ControlledAccordion.displayName = 'ControlledAccordion';

// Props are documented in `Accordion.jsx`
ControlledAccordion.propTypes = process.env.NODE_ENV !== "production" ? {
  children: _propTypes.default.node.isRequired,
  expanded: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.arrayOf(_propTypes.default.number)]),
  onChange: _propTypes.default.func,
  onPanelFocus: _propTypes.default.func
} : {};